import { useState } from "react";
import { Alert, Button, Icon, Input, Modal, PasswordInput, Select, Space, Text, useToast } from "@app/ui";
import { api } from "../api/client.js";
import { addOrgMember } from "../api/dpp.js";
import { PasswordChecklist, passwordMeetsPolicy } from "../auth/PasswordChecklist.js";
import { ErrorAlert } from "../errors/index.js";
import { useT } from "../i18n.js";
import type { OrgMemberRole, OrgWithCounts, PlatformRole, PublicUser } from "../types.js";

const ORG_ROLES: readonly OrgMemberRole[] = ["admin", "editor", "viewer"];

/** A random password that satisfies the policy (length 16, all four classes). */
export function generatePassword(): string {
  const sets = ["abcdefghijkmnpqrstuvwxyz", "ABCDEFGHJKLMNPQRSTUVWXYZ", "23456789", "!@#$%*-_+"];
  const all = sets.join("");
  const pick = (chars: string) => chars[crypto.getRandomValues(new Uint32Array(1))[0]! % chars.length]!;
  const chars = [...sets.map(pick), ...Array.from({ length: 12 }, () => pick(all))];
  // Shuffle so the guaranteed class characters aren't always in front.
  for (let i = chars.length - 1; i > 0; i--) {
    const j = crypto.getRandomValues(new Uint32Array(1))[0]! % (i + 1);
    [chars[i], chars[j]] = [chars[j]!, chars[i]!];
  }
  return chars.join("");
}

/**
 * Platform admin: create an account with an initial password, optionally a
 * platform role and a first org membership. The password is shown once on
 * success so the admin can hand it over; the user changes it from Profile.
 */
export function CreateUserModal({
  open,
  onClose,
  roles,
  orgs,
}: {
  open: boolean;
  onClose: (created: boolean) => void;
  roles: PlatformRole[];
  orgs: OrgWithCounts[];
}) {
  const t = useT();
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("");
  const [orgId, setOrgId] = useState("");
  const [orgRole, setOrgRole] = useState<OrgMemberRole>("admin");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<{ email: string; password: string } | null>(null);

  const reset = () => {
    setEmail("");
    setDisplayName("");
    setPassword("");
    setRole("");
    setOrgId("");
    setOrgRole("admin");
    setError(null);
    setDone(null);
  };

  const close = () => {
    const created = done != null;
    reset();
    onClose(created);
  };

  const submit = async () => {
    setError(null);
    setSaving(true);
    try {
      const user = await api<PublicUser>("/admin/users", {
        method: "POST",
        body: {
          email: email.trim(),
          password,
          ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
          ...(role ? { roles: [role] } : {}),
        },
      });
      if (orgId) {
        try {
          await addOrgMember(orgId, user.id, orgRole);
        } catch (e) {
          // The account exists; only the membership failed — say so, don't roll back.
          toast.error(t("adminUsers.orgAddFailed"));
          setError(e);
        }
      }
      setDone({ email: user.email, password });
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = email.trim().length > 3 && passwordMeetsPolicy(password);

  return (
    <Modal
      open={open}
      onClose={close}
      title={t("adminUsers.create")}
      width={560}
      footer={
        done ? (
          <Button onClick={close}>{t("common.close")}</Button>
        ) : (
          <Space size="sm">
            <Button variant="subtle" onClick={close}>
              {t("common.cancel")}
            </Button>
            <Button onClick={() => void submit()} loading={saving} disabled={!canSubmit}>
              {t("common.create")}
            </Button>
          </Space>
        )
      }
    >
      {done ? (
        <Space direction="vertical" size="md" style={{ width: "100%" }}>
          <Alert type="success">{t("adminUsers.created")}</Alert>
          <Text>{t("adminUsers.handOver")}</Text>
          <Input label={t("common.email")} value={done.email} readOnly />
          <Input label={t("adminUsers.password")} value={done.password} readOnly />
          {error != null && <ErrorAlert error={error} />}
        </Space>
      ) : (
        <Space direction="vertical" size="md" style={{ width: "100%" }}>
          {error != null && <ErrorAlert error={error} />}
          <Input
            label={t("common.email")}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            label={t("adminUsers.displayName")}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
          <div>
            <PasswordInput
              label={t("adminUsers.password")}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <div className="flex items-center justify-between gap-2" style={{ marginTop: 6 }}>
              <PasswordChecklist value={password} />
              <Button
                size="sm"
                variant="subtle"
                icon={<Icon name="key" size={14} />}
                onClick={() => setPassword(generatePassword())}
              >
                {t("adminUsers.generate")}
              </Button>
            </div>
          </div>
          <Select
            label={t("adminUsers.platformRole")}
            help={t("adminUsers.platformRoleHelp")}
            options={[
              { label: t("adminUsers.noPlatformRole"), value: "" },
              ...roles.map((r) => ({ label: r.name, value: r.name })),
            ]}
            value={role}
            onChange={setRole}
          />
          <Select
            label={t("adminUsers.firstOrg")}
            showSearch
            options={[
              { label: t("adminUsers.noOrg"), value: "" },
              ...orgs.map((o) => ({ label: o.name, value: o.id, searchText: o.slug })),
            ]}
            value={orgId}
            onChange={setOrgId}
          />
          {orgId && (
            <Select
              label={t("orgSettings.role")}
              options={ORG_ROLES.map((r) => ({ label: t(`role.${r}`), value: r }))}
              value={orgRole}
              onChange={(v) => setOrgRole(v as OrgMemberRole)}
            />
          )}
        </Space>
      )}
    </Modal>
  );
}
